<template>
  <v-dialog v-model="dialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '900'" :fullscreen="$vuetify.breakpoint.xsOnly" persistent>
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title class="white--text">
          {{ editingDispatchNoteId ? $t('inventory.edit_dispatch_note') : $t('inventory.new_dispatch_note') }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon dark :aria-label="$t('close')" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pt-5 gradient-icons">
        <v-form ref="dispatchForm" v-model="dispatchFormValid">
          <v-row v-if="!site">
            <v-col cols="12">
              <site-combobox
                :icon="false"
                :hide-details="false"
                :multiple="false"
                :label="$t('timer.site')"
                :sites="selectableSites"
                :value="selectedSite"
                :required="true"
                @change="selectedSite = $event"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="dispatchForm.warehouseId"
                :items="warehouses"
                item-text="name"
                item-value="id"
                :label="$t('inventory.warehouse')"
                :rules="[v => !!v || $t('validation.required')]"
                prepend-inner-icon="mdi-warehouse"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-radio-group v-model="dispatchForm.direction" row mandatory>
                <v-radio
                  :label="$t('inventory.directions.TO_SITE')"
                  value="TO_SITE"
                  color="success"
                />
                <v-radio
                  :label="$t('inventory.directions.FROM_SITE')"
                  value="FROM_SITE"
                  color="warning"
                />
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="4">
              <v-menu
                v-model="dispatchForm.dispatchDateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="formatDateFi(dispatchForm.dispatchDate)"
                    :label="$t('inventory.dispatch_date')"
                    readonly outlined dense hide-details
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs" v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="dispatchForm.dispatchDate"
                  no-title
                  :first-day-of-week="1"
                  locale="fi"
                  @input="dispatchForm.dispatchDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <v-divider class="mb-4" />

          <template v-for="(row, index) in dispatchForm.items">
            <v-row :key="index" align="center" class="mb-4">
            <v-col cols="10" sm="5">
              <item-combobox
                v-model="row.item"
                :types="trackableTypes"
                :label="$t('inventory.select_item')"
                required
                hide-details="auto"
              />
            </v-col>
            <v-col cols="2" class="d-sm-none text-right">
              <v-btn icon color="error" :aria-label="$t('inventory.remove_row')" @click="removeDispatchItem(index)" :disabled="dispatchForm.items.length === 1">
                <v-icon class="no-gradient">mdi-delete</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="6" sm="2">
              <v-text-field
                v-model.number="row.pieces"
                :label="$t('inventory.pieces')"
                type="number"
                min="1"
                :rules="[v => !!v && v > 0 || $t('validation.positive_number')]"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6" sm="3" v-if="row.item && row.item.unit === 'm'">
              <v-text-field
                v-model.number="row.unitQuantity"
                :label="$t('inventory.meters_per_piece')"
                type="number"
                step="0.1"
                min="0.1"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
            <v-col cols="auto" class="ml-auto d-none d-sm-flex">
              <v-btn icon color="error" :aria-label="$t('inventory.remove_row')" @click="removeDispatchItem(index)" :disabled="dispatchForm.items.length === 1">
                <v-icon class="no-gradient">mdi-delete</v-icon>
              </v-btn>
            </v-col>
            </v-row>
          </template>

          <v-row>
            <v-col cols="12">
              <v-btn text color="primary" @click="addDispatchItem">
                <v-icon left>mdi-plus</v-icon>
                {{ $t('inventory.add_item') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider />
      <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
        <v-spacer />
        <v-btn text :block="$vuetify.breakpoint.xsOnly" @click="close">{{ $t('cancel') }}</v-btn>
        <v-btn
          color="primary"
          :block="$vuetify.breakpoint.xsOnly"
          :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
          :disabled="!dispatchFormValid"
          :loading="savingDispatch"
          @click="saveDispatchNote"
        >
          {{ editingDispatchNoteId ? $t('save') : $t('inventory.create_dispatch') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <confirm-dialog ref="confirmDialog" />
  </v-dialog>
</template>

<script>
import inventoryApi from '@/api/inventory';
import ConfirmDialog from '@/components/ConfirmDialog';
import ItemCombobox from '@/components/ItemCombobox';
import SiteCombobox from '@/components/SiteCombobox';
import { activeTypeIds } from '@/helpers/itemTypes';
import { defaultWarehouseId, rememberWarehouseId } from '@/helpers/selectedWarehouse';

export default {
  name: 'DispatchNoteDialog',
  components: { ConfirmDialog, ItemCombobox, SiteCombobox },
  props: {
    // Kiinteä työmaa (esim. työmaasivulta avattaessa). Jos null, dialogi näyttää työmaan valinnan.
    site: {
      type: Object,
      default: null,
    },
    // Valittavissa olevat työmaat, kun kiinteää työmaata ei ole annettu.
    sites: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialog: false,
      savingDispatch: false,
      warehouses: [],
      selectedSite: null,
      dispatchFormValid: false,
      editingDispatchNoteId: null,
      editingCompleted: false,
      dispatchForm: {
        warehouseId: null,
        direction: 'TO_SITE',
        dispatchDate: new Date().toISOString().substr(0, 10),
        dispatchDateMenu: false,
        items: [{ item: null, pieces: null, unitQuantity: null }],
      },
    };
  },
  computed: {
    trackableTypes() {
      return activeTypeIds(type => type.trackInventory);
    },
    selectableSites() {
      return this.sites.filter(
        site => site.status !== 'BILLED' && site.status !== 'DELETED' && site.status !== 'DONE'
      );
    },
  },
  methods: {
    // Varastot haetaan joka avauksella, jotta uudet ja uudelleennimetyt näkyvät.
    async loadWarehouses() {
      try {
        this.warehouses = await inventoryApi.getWarehouses();
      } catch (error) {
        console.error('Error loading warehouses:', error);
        this.showError(this.$t('inventory.errors.load_warehouses'));
      }
    },

    open() {
      this.editingDispatchNoteId = null;
      this.editingCompleted = false;
      this.selectedSite = null;
      this.dispatchForm = {
        warehouseId: null,
        direction: 'TO_SITE',
        dispatchDate: new Date().toISOString().substr(0, 10),
        dispatchDateMenu: false,
        items: [{ item: null, pieces: null, unitQuantity: null }],
      };
      this.dialog = true;
      this.loadWarehouses().then(() => {
        this.dispatchForm.warehouseId = defaultWarehouseId(this.warehouses);
      });
    },

    openEdit(dispatchNote) {
      this.editingDispatchNoteId = dispatchNote.id;
      this.editingCompleted = false;
      this.selectedSite = null;
      this.loadWarehouses();
      this.dispatchForm = {
        warehouseId: dispatchNote.warehouseId,
        direction: dispatchNote.direction,
        dispatchDate: dispatchNote.createdAt ? dispatchNote.createdAt.substr(0, 10) : new Date().toISOString().substr(0, 10),
        dispatchDateMenu: false,
        items: dispatchNote.items.map(item => ({
          item: item.item,
          pieces: item.pieces,
          unitQuantity: item.unitQuantity,
        })),
      };
      this.dialog = true;
    },

    openEditCompleted(dispatchNote) {
      this.openEdit(dispatchNote);
      this.editingCompleted = true;
    },

    close() {
      this.dialog = false;
      this.editingDispatchNoteId = null;
      this.editingCompleted = false;
    },

    addDispatchItem() {
      this.dispatchForm.items.push({ item: null, pieces: null, unitQuantity: null });
    },

    removeDispatchItem(index) {
      this.dispatchForm.items.splice(index, 1);
    },

    async saveDispatchNote() {
      if (!this.$refs.dispatchForm.validate()) return;

      const targetSite = this.site || this.selectedSite;
      if (!targetSite) return;

      const isCompleted = this.editingCompleted;

      if (isCompleted) {
        const confirmed = await this.$refs.confirmDialog.open(
          this.$t('inventory.confirm_edit_completed.title'),
          this.$t('inventory.confirm_edit_completed.message'),
          { color: 'warning', info: this.$t('inventory.edit_completed_info') }
        );
        if (!confirmed) return;
      }

      this.savingDispatch = true;
      try {
        const data = {
          warehouseId: this.dispatchForm.warehouseId,
          direction: this.dispatchForm.direction,
          dispatchDate: this.dispatchForm.dispatchDate,
          siteId: targetSite.id,
          items: this.dispatchForm.items.map(row => ({
            itemId: row.item.id,
            pieces: row.pieces,
            unitQuantity: row.unitQuantity,
          })),
        };
        rememberWarehouseId(this.dispatchForm.warehouseId);

        if (isCompleted) {
          await inventoryApi.updateCompletedDispatchNote(this.editingDispatchNoteId, data);
          this.showSuccess(this.$t('inventory.success.dispatch_updated'));
        } else if (this.editingDispatchNoteId) {
          await inventoryApi.updateDispatchNote(this.editingDispatchNoteId, data);
          this.showSuccess(this.$t('inventory.success.dispatch_updated'));
        } else {
          await inventoryApi.createDispatchNote(data);
          this.showSuccess(this.$t('inventory.success.dispatch_created'));
        }

        const completedEdit = isCompleted;
        this.close();
        this.$emit('saved', { completedEdit });
      } catch (error) {
        console.error('Error saving dispatch note:', error);
        this.showError(this.editingDispatchNoteId
          ? this.$t('inventory.errors.dispatch_update_failed')
          : this.$t('inventory.errors.dispatch_create_failed'));
      } finally {
        this.savingDispatch = false;
      }
    },

    formatDateFi(dateStr) {
      if (!dateStr) return '';
      const [y, m, d] = dateStr.split('-');
      return `${d}.${m}.${y}`;
    },

    showSuccess(message) {
      this.$store.dispatch('showNotification', {
        color: 'success',
        message: message,
        timeOut: 2000,
      });
    },

    showError(message) {
      this.$store.dispatch('showNotification', {
        color: 'error',
        message: message,
        timeOut: 3000,
      });
    },
  },
};
</script>
